import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import last from 'lodash/last';

import { WorkspaceType } from 'Shared/constants/types';
import { Illustrations } from 'Wpds/components/illustration';
import { isEmptyString, validEmail, showNotificationError, fullNameFromEmail } from 'Shared/helpers';
import { getOrganizationDomain } from 'Shared/selectors/organization';
import { bulkCreateWorkspaces } from 'Shared/actions/workspaces';
import SimpleAddWorkspaceWrapper from 'Shared/components/modals/SimpleAddWorkspaceModals/SimpleAddWorkspaceWrapper/SimpleAddWorkspaceWrapper';
import AddTeammateSuggestionsSection from 'Shared/components/modals/SimpleAddWorkspaceModals/SuggestionsSection/AddTeammateSuggestionsSection/AddTeammateSuggestionsSection';
import QueryParamConstants from 'Shared/constants/QueryParams.constants';
import { billingStates } from 'Shared/constants/constants';
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Hooks
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';

import CreateNameSection from '../AddTeammateModalWithCalendarIntegration/CreateNameSection/CreateNameSection';

import './CreateMoreWorkspacesModal.scss';

type Props = {
  onClose: Function;
  title?: string;
  createNameSectionTitle?: string;
  alert?: React.ReactNode;
  includeSuggestions?: boolean;
  calendarMeetingId?: string;
  defaultInvitees?: {
    fullName: string;
    email: string;
    recurringMeetingId?: number;
    fromCalendar: boolean;
    calendarMeetingId?: string;
  }[];
  inviteMethod?: string;
};

function CreateMoreWorkspacesModal({
  onClose,
  alert,
  includeSuggestions = true,
  title = 'Create more workspaces',
  createNameSectionTitle = 'Or add and invite',
  defaultInvitees,
  inviteMethod = TrackingConstants.INVITE_METHOD_TYPES.CREATE_ONE_ON_ONE_WORKSPACE_MODAL,
}: Props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const freeTrialActive = useSelector(state => state.organization.billingState === billingStates.FREE_TRIAL);
  const organizationDomain = useSelector(getOrganizationDomain);
  const { organization } = useOrganization();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const [isSuggestedListOpened, setIsSuggestedListOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({
    0: {
      fullName: '',
      email: organizationDomain,
      userId: null,
      recurringMeetingId: null,
      calendarMeetingId: null,
      errorMessage: null,
      fromCalendar: false,
    },
  });

  const isFromConnectToCal = useCallback(search.includes(QueryParamConstants.FROM_CONNECT_TO_CAL), [search]);

  const disabledSubmit = isSubmitting;

  const validateFields = ({ fullName, email }) => {
    if (email.startsWith('@')) return null;
    if (isEmptyString(fullName)) return 'Please enter a full name';
    if (email !== organizationDomain && !validEmail(email)) return `Invalid email address: ${email}`;
    if (!isEmployeeEmailOrValidDomain({ email }))
      return `Invalid email domain, must be one of the following domains: ${organization.pi_allowed_domains.join(
        ', '
      )}`;
    return null;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const checkedformValues: any = {};
      Object.keys(formValues).forEach(key => {
        checkedformValues[key] = { ...formValues[key], errorMessage: validateFields(formValues[key]) };
      });
      if (Object.values(checkedformValues).find(({ errorMessage }) => errorMessage)) {
        setFormValues(checkedformValues);
        return null;
      }

      const workspacesParams = Object.values(checkedformValues).map(
        (value: {
          fullName: string;
          email: string;
          recurringMeetingId: number;
          calendarMeetingId: string;
          fromCalendar: boolean;
        }) => {
          const inviteeEmail = value.email !== organizationDomain ? value.email : null;
          return {
            name: value.fullName,
            inviteeEmails: [inviteeEmail],
            recurringMeetingId: value.recurringMeetingId,
            calendarMeetingId: value.recurringMeetingId ? null : value?.calendarMeetingId,
            inviteeEmailsFromCalendar: value.fromCalendar ? [inviteeEmail] : [null],
          };
        }
      );

      const firstCreatedWorkspace = await dispatch(
        bulkCreateWorkspaces({
          workspacesParams,
          inviteMethod,
        })
      );
      if (firstCreatedWorkspace?.path) {
        onClose();
        history.push(firstCreatedWorkspace?.path);
      }
    } catch (e) {
      showNotificationError(e);
    } finally {
      setIsSubmitting(false);
    }
    return null;
  };

  const handleSuggestedMeetingClick = ({ recurringMeetingId, userId, email, fullName }) => {
    const lastKey = last(Object.keys(formValues));
    const lastField = formValues[lastKey];
    let newKey;
    if (
      isEmptyString(lastField.fullName) &&
      (isEmptyString(lastField.email) || lastField.email === organizationDomain)
    ) {
      newKey = lastKey;
    } else {
      newKey = parseInt(lastKey, 10) + 1;
    }

    (window as any).analytics.track('Clicked in Add Workspace Modal', {
      modal: 'add_teammate',
      button: 'Meeting',
    });

    setFormValues({
      ...formValues,
      [newKey]: {
        recurringMeetingId,
        email: email || '',
        userId,
        fullName: fullName || fullNameFromEmail(email) || '',
        fromCalendar: true,
      },
    });
  };

  useEffect(() => {
    if (defaultInvitees) {
      const newObj = {};
      defaultInvitees.forEach((invitee, index) => {
        newObj[index] = {
          ...invitee,
          errorMessage: null,
          userId: null,
        };
      });
      // @ts-ignore
      setFormValues(newObj);
    }
  }, [defaultInvitees]);

  return (
    <SimpleAddWorkspaceWrapper
      onClose={onClose}
      workspaceType={WorkspaceType.isTeammate}
      title={title}
      alert={alert}
      description={
        isFromConnectToCal ? (
          <div className="font-weight-normal">
            We couldn’t find any meetings in your calendar. Let’s create a workspaces to start collaborating with your
            team.{' '}
            {freeTrialActive && (
              <div className="simple-add-teammate-modal-highlight-description">
                Add a teammate to extend your trial for 7 days.
              </div>
            )}
          </div>
        ) : (
          ''
        )
      }
      disabledSubmit={disabledSubmit}
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit}
      createNameSection={
        <CreateNameSection
          formValues={Object.values(formValues)}
          isSuggestedListOpened={isSuggestedListOpened}
          onSetIsSuggestedListOpened={setIsSuggestedListOpened}
          onSetFormValues={setFormValues}
        />
      }
      createNameSectionTitle={createNameSectionTitle}
      suggestionsSection={
        includeSuggestions && (
          <AddTeammateSuggestionsSection
            formValues={formValues}
            onSuggestedMeetingClick={handleSuggestedMeetingClick}
          />
        )
      }
      illustration={Illustrations.two_people_standing_with_laptop_purple_bg}
    />
  );
}

export default CreateMoreWorkspacesModal;
